import { CheckBox } from '@design/components/checkbox/CheckBox';
import { Dropdown, DropdownProps } from '@design/components/dropdown/Dropdown';
import { DropdownListItem } from '@design/components/dropdown/dropdown-list-item/DropdownListItem';
import { Icon } from '@design/components/icon/Icon';
import { Spinner } from '@design/components/spinner/Spinner';
import { useWatchlistActions } from '@terminal/hooks/use-watchlist-actions/useWatchlistActions';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { Watchlist } from '@terminal/stores/use-watchlist/watchlist-types';
import { ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { CreateWatchlistModal } from '../create-watchlist-modal/CreateWatchlistModal';
import styles from './WatchlistTrigger.module.scss';

interface WatchlistTriggerProps {
  tag: string;
  renderTrigger: (isInWatchlist: boolean) => ReactElement;
  dropdownProps?: Partial<DropdownProps<Watchlist>>;
}

export function WatchlistTrigger({
  tag,
  renderTrigger,
  dropdownProps,
}: WatchlistTriggerProps) {
  const { t } = useTranslation('widget');
  const [displayCreateModal, setDisplayCreateModal] = useState(false);
  const [shouldCloseOnOutsideClick, setShouldCloseOnOutsideClick] =
    useState(true);

  const {
    createNewList,
    watchlistErrorText,
    clearError,
    createListLoading,
    hasExceededUsageLimit,
  } = useWatchlistActions();
  const { lists, listIdsWithTag, addItemToList, removeItemFromList } =
    // passing equity fn has been deprecated
    // but it's required here to rerender this component only when
    // either list is created or tag is added/removed
    useStoreWithEqualityFn(
      useWatchlist,
      state => ({
        lists: state.lists,
        addItemToList: state.addItemToList,
        removeItemFromList: state.removeItemFromList,
        listIdsWithTag: state.listIdsByTag.get(tag),
      }),
      (current, prev) =>
        current.lists.length === prev.lists.length &&
        current.listIdsWithTag === prev.listIdsWithTag
    );

  const closeModal = () => {
    setShouldCloseOnOutsideClick(true);
    setDisplayCreateModal(false);
    clearError();
  };

  const onCreateNewListClick = () => {
    setShouldCloseOnOutsideClick(false);
    setDisplayCreateModal(true);
  };

  const submitNewList = async (name: string) => {
    const success = await createNewList(name, [tag]);

    if (!!success) {
      closeModal();
    }
  };

  const toggleWatchlist = async (watchlistId: string, hasTag: boolean) => {
    if (hasTag) {
      await removeItemFromList(watchlistId, tag);
    } else {
      await addItemToList(watchlistId, tag);
    }
  };

  const includedInWatchlistIds = listIdsWithTag || [];
  const isInAnyWatchlist = !!includedInWatchlistIds.length;
  const hasNoWatchlist = useMemo(() => !lists.length, [lists.length]);
  return (
    <div>
      {displayCreateModal && (
        <CreateWatchlistModal
          isLoading={createListLoading}
          onClose={closeModal}
          onSubmit={submitNewList}
          watchlistErrorText={watchlistErrorText}
          clearError={clearError}
          hasExceededUsageLimit={hasExceededUsageLimit}
        />
      )}
      <Dropdown<Watchlist>
        className={styles.watchlistDropdown}
        beforeInListNode={
          createListLoading ? (
            <div className={styles.loadingWrapper}>
              <Spinner size="small" />
            </div>
          ) : (
            <>
              <DropdownListItem
                data-testid="create-new-list-btn"
                onClick={onCreateNewListClick}
                label={t('widget:watchlistTrigger.createListBtnLabel')}
                leadingIcon={<Icon iconName={'Add'} size={16} />}
                isActive={false}
                isFocused={false}
              />
              {lists.length > 0 && <hr className={styles.dropdownDivider} />}
            </>
          )
        }
        onClick={() => {
          hasNoWatchlist && onCreateNewListClick();
        }}
        dropdownItems={lists}
        shouldCloseOnSelect={false}
        shouldCloseOnOutsideClick={shouldCloseOnOutsideClick}
        shouldShowItems={false}
        renderDropdownItem={item => {
          const hasTag = includedInWatchlistIds.includes(item.id);
          return (
            <div
              className={styles.itemRow}
              key={item.id}
              data-testid="dropdown-item"
            >
              <CheckBox
                className={styles.dropdownCheckbox}
                data-testid="column-checkbox"
                name={item.id}
                label={item.name}
                checked={hasTag}
                onChange={() => toggleWatchlist(item.id, hasTag)}
              />
            </div>
          );
        }}
        inPortal
        {...dropdownProps}
      >
        {renderTrigger(isInAnyWatchlist)}
      </Dropdown>
    </div>
  );
}
